import dayjs from "dayjs";

export const NUMBER_REGEX = /^[0-9]+$/;
export const TIME_REGEX = /^(0[0-9]|1[0-9]|2[0-3]):([0-5][0-9])$/;
export const DATE_FORMAT = "YYYY-MM-DD";

/**
 * Combine URL segments
 *
 * @param baseURL The base url
 * @param relativeURL Relative path sections
 * @returns Final URL
 */
export function combineURL(baseURL: string, relativeURL: string): string {
	return relativeURL ? `${baseURL.replace(/\/+$/, "")}/${relativeURL.replace(/^\/+/, "")}` : baseURL;
}

/**
 * Get the value from an input event
 *
 * @param event The event
 * @returns Current value
 */
export function getInputValue(event: InputEvent): string {
	return event.target ? (event.target as HTMLInputElement).value : "";
}

/**
 * Combine string segments
 */
export function concat(segments: string[]) {
	const sgmts = [...segments];
	const last = sgmts.pop();
	return sgmts.join(", ") + (sgmts.length > 0 ? " en " : "") + last;
}

/**
 * Convert the given text to a slug
 */
export function slug(text: string) {
	return text
		.toLowerCase()
		.replace(/[^\w ]+/g, "")
		.replace(/ +/g, "-");
}

export function isValidDate(date: string): boolean {
	return dayjs(date, DATE_FORMAT, true).isValid();
}

/**
 * converts a formatted date string to an ISO string
 *
 * @param format The format of the formatted date string (default: "YYYY-MM-DD")
 */
export function toISOString(formattedDate: string, format: string = DATE_FORMAT): string {
	return dayjs(formattedDate, format, true).toISOString();
}

/**
 * Converts a date string string to a formatted date string
 *
 * @param dateString The date string to convert
 * @param format The format of the passed date string (default: "DD-MM-YYYY")
 */
export function toFormattedDateString(dateString: string, format: string = DATE_FORMAT): string {
	return dayjs(dateString, format, true).format("DD-MM-YYYY");
}

/**
 * Get the accent color from the variable accent color
 *
 * @returns The variable accent color as hex string
 */
export function getAccentColor(element: Element): string {
	return getComputedStyle(element).getPropertyValue("--primary");
}

/**
 * Stop the event propagation.
 * @param event
 */
export function stopPropagation(event: Event) {
	event.stopPropagation();
}

/**
 * Prevent the default event behavior.
 * @param event
 */
export function preventDefault(event: Event) {
	event.preventDefault();
}

/**
 * Assign a attribute value to an element
 */
export function attrv(value: any): "" | undefined {
	return value ? value : undefined;
}

/**
 * Capitalize the first word of the string
 *
 * capitalize('hello')   -> 'Hello'
 * capitalize('va va voom') -> 'Va va voom'
 */
export const capitalize = (str: string): string => {
	if (!str || str.length === 0) {
		return "";
	}

	const lower = str.toLowerCase();

	const lowerLeft = lower.substring(0, 1).toUpperCase();
	const lowerRight = lower.substring(1, lower.length);

	return lowerLeft + lowerRight;
};

/**
 * Formats the given string in snake case fashion
 *
 * snake('hello world')   -> 'hello_world'
 * snake('va va-VOOM') -> 'va_va_voom'
 * snake('helloWord') -> 'hello_world'
 */
export const snake = (
	str: string,
	options?: {
		splitOnNumber?: boolean;
	},
): string => {
	const parts =
		str
			.replace(/([A-Z])+/g, capitalize)
			.split(/(?=[A-Z])|[\.\-\s_]/)
			.map((x) => x.toLowerCase()) ?? [];

	if (parts.length === 0) return "";
	if (parts.length === 1) return parts[0];

	const result = parts.reduce((acc, part) => {
		return `${acc}_${part.toLowerCase()}`;
	});

	return options?.splitOnNumber === false
		? result
		: result.replace(/([A-Za-z]{1}[0-9]{1})/, (val) => `${val[0]}_${val[1]}`);
};

/**
 * An function that returns an empty object.
 * @returns
 */
export function EmptyFnObj<T>(): T {
	return {} as T;
}
